import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Avatar } from '@components/Avatar/Avatar';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { SendIcon } from '@dynamika-shared/ui/icons';
import { Divider } from 'antd';
import { format, isToday } from 'date-fns';
import { useParams } from 'react-router-dom';
import { twMerge } from '@utils/twMerge';
import { useQCTaskCommentsSlice } from '@restore/QCTaskComments/QCTaskComments.slice';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { sortByCreatedAsc } from '@utils/sortOptions';

const MAX_NOTE_LENGTH = 700;

export const QcNotes = () => {
  const userData = useAuthSlice(s => s.data);
  const { id: clinicalTrialId, taskId } = useParams<{
    id: string;
    taskId: string;
  }>();
  const [commentsData, addComment] = useQCTaskCommentsSlice(
    (state, actions) => [state.data, actions.addComment]
  );
  const [comment, setComment] = useState('');
  const notesContainerRef = useRef<HTMLDivElement | null>(null);

  const sortedComments = useMemo(
    () => [...(commentsData ?? [])].sort(sortByCreatedAsc),
    [commentsData]
  );

  const inputInvalid =
    !comment?.trim()?.length || comment?.trim()?.length > MAX_NOTE_LENGTH;

  const handleSendComment = useCallback(() => {
    if (!inputInvalid) {
      if (clinicalTrialId && taskId) {
        addComment({ clinicalTrialId, taskId, message: comment });
      }
      setComment('');
    }
  }, [clinicalTrialId, comment, taskId]);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !inputInvalid) {
        handleSendComment();
      }
    },
    [handleSendComment, inputInvalid]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    if (notesContainerRef.current) {
      notesContainerRef.current.scrollTop =
        notesContainerRef.current.scrollHeight;
    }
  }, [commentsData?.length]);
  return (
    <div className='flex h-[calc(100vh-400px)] w-[100%] flex-col'>
      <div className='mb-[22px] flex items-center justify-between font-medium rounded-[8px] bg-[#F6F9FF] p-[12px] text-1824 border-[1px] border-brightGray'>
        Notes
      </div>
      <div ref={notesContainerRef} className='flex-1 overflow-auto px-1'>
        {sortedComments.map(({ writerFullName, id, created, message }) => {
          const createdDate = new Date(created);
          return (
            <div key={id}>
              <div className='flex items-start gap-2'>
                <div>
                  <Avatar userName={writerFullName} nameSeparator={' '} />
                </div>
                <div className=''>
                  <div className='pb-1 text-1216 font-medium'>
                    <div>{writerFullName}</div>
                    <div>
                      <span className='text-1216 text-spanishGray'>
                        {isToday(createdDate)
                          ? format(createdDate, '\'Today\' HH:mm')
                          : format(createdDate, 'dd-MMM-yyyy HH:mm')}
                      </span>
                    </div>
                  </div>
                  <div className='break-all pr-2 text-1624 font-medium'>
                    {message}
                  </div>
                </div>
              </div>
              <Divider className='my-[16px]' />
            </div>
          );
        })}
      </div>
      <div className='flex w-full items-center gap-2 px-1 mb-2'>
        <Avatar userName={`${userData?.nickname}`} nameSeparator={'.'} />
        <Input
          placeholder='Add a note'
          value={comment}
          maxLength={MAX_NOTE_LENGTH}
          onChange={({ target: { value } }) => setComment(value)}
          className='!mt-0 w-full'
          classNameInputWrapper='max-w-[100%]'
          suffix={
            <SendIcon
              onClick={handleSendComment}
              className={twMerge(
                'cursor-pointer text-[24px]',
                inputInvalid &&
                  'stroke-dimGray ![&>path]:fill-dimGray opacity-[0.3]'
              )}
            />
          }
        />
      </div>
    </div>
  );
};
